import React, { useState, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    LinearProgress,
    IconButton,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import api from '../api/axios';

interface FileUploadDialogProps {
    open: boolean;
    onClose: () => void;
    patientId: string;  // This is the local ID from the URL
    onUploadComplete: (data?: any) => void;
}

const FileUploadDialog: React.FC<FileUploadDialogProps> = ({
    open,
    onClose,
    patientId,  // This is already the local ID from the URL
    onUploadComplete
}) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [description, setDescription] = useState('');
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState('');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
            setError('');
        }
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            setError('Please select a file');
            return;
        }

        setUploading(true);
        setError('');

        try {
            // Create FormData object for file upload
            const formData = new FormData();
            formData.append('file', selectedFile);
            formData.append('description', description);
            formData.append('patientId', patientId);

            // Upload using FormData (multipart/form-data)
            const response = await api.post('/api/patient-attachments', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            // Call onUploadComplete with response data to immediately update UI
            onUploadComplete(response.data);
            handleClose();
        } catch (err: any) {
            console.error('Upload error:', err);
            setError(err.response?.data?.error || 'Failed to upload file');
        } finally {
            setUploading(false);
        }
    };

    const handleClose = () => {
        setSelectedFile(null);
        setDescription('');
        setError('');
        setUploading(false);
        onClose();
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    Upload Attachment
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2 }}>
                    <input
                        type="file"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                        accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                    />
                    <Box
                        sx={{
                            border: '2px dashed',
                            borderColor: 'primary.main',
                            borderRadius: 1,
                            p: 3,
                            textAlign: 'center',
                            cursor: 'pointer',
                            '&:hover': {
                                bgcolor: 'action.hover'
                            }
                        }}
                        onClick={() => fileInputRef.current?.click()}
                    >
                        <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
                        <Typography>
                            {selectedFile ? selectedFile.name : 'Click to select file'}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                            Supported formats: PDF, DOC, DOCX, TXT, JPG, PNG
                        </Typography>
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                            Note: Files will be stored in Hydralyfe. Patient must have a Hydralyfe ID.
                        </Typography>
                    </Box>

                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        label="Description (optional)"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        sx={{ mt: 2 }}
                        disabled={uploading}
                    />

                    {error && (
                        <Typography color="error" sx={{ mt: 1 }}>
                            {error}
                        </Typography>
                    )}

                    {uploading && (
                        <Box sx={{ mt: 2 }}>
                            <LinearProgress />
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={uploading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleUpload}
                    variant="contained"
                    disabled={!selectedFile || uploading}
                >
                    {uploading ? 'Uploading...' : 'Upload'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FileUploadDialog; 